// Removed incorrect import of Error icon
import { soulApi } from "../api/SoulApi";
import {
  ApplicationRequest,
  ApplicationAdminRequest,
  CreditApplication,
  InstallmentRequest,
  UpdateInstallmentRequest,
} from "../models/applications/CreditApplication";
import {
  SoulDataModel,
  UpdateSoulInfoPayload,
} from "../models/Soul/SoulDataModel";

class SoulManager {
  private static instance: SoulManager;

  public static get(): SoulManager {
    if (!this.instance) {
      this.instance = new SoulManager();
    }
    return this.instance;
  }

  async getSoul(): Promise<SoulDataModel | undefined> {
    try {
      const result = await soulApi.soulInfo.request(null);
      return new SoulDataModel(result.data);
    } catch (error) {
      console.error("Error en getSoul:", error);
      return undefined;
    }
  }

  async getAllSouls(): Promise<SoulDataModel[] | undefined> {
    try {
      const result = await soulApi.soulsInfo.request(null);
      const data = result?.data;
      if (!Array.isArray(data)) {
        console.error("Esperaba un array en result.data");
        return undefined;
      }
      return data.map((item) => new SoulDataModel(item));
    } catch (error) {
      console.error("Error en getAllSouls:", error);
      return undefined;
    }
  }

  async getAllSoulsGuarantors(): Promise<SoulDataModel[] | undefined> {
    try {
      const result = await soulApi.soulsGuarantors.request(null);
      const data = result?.data;
      if (!Array.isArray(data)) {
        console.error("Esperaba un array en result.data");
        return undefined;
      }
      return data.map((item) => new SoulDataModel(item));
    } catch (error) {
      console.error("Error en getAllSoulsGuarantors:", error);
      return undefined;
    }
  }

  async updateSoulInfo(
    payload: UpdateSoulInfoPayload
  ): Promise<SoulDataModel | undefined> {
    try {
      const result = await soulApi.updateSoulInfo.request(payload);
      return new SoulDataModel(result.data);
    } catch (error) {
      console.error("Error en updateSoulInfo:", error);
      return undefined;
    }
  }

  async updateSoulInfoByAdmin(
    payload: UpdateSoulInfoPayload
  ): Promise<boolean | undefined> {
    try {
      await soulApi.updateSoulInfoByAdmin.request(payload);
      return true;
    } catch (error) {
      console.error("Error en updateSoulInfoByAdmin:", error);
      return undefined;
    }
  }

  async getAllApplications(): Promise<CreditApplication[] | undefined> {
    try {
      const result = await soulApi.soulApplications.request(null);
      console.log(result);
      if (!result?.data) {
        throw new Error("Error on getAllApplications");
      }

      return result.data as CreditApplication[];
    } catch (error) {
      console.error("Error en getAllApplications:", error);
      return undefined;
    }
  }

  async getAllApplicationsByAdmin(): Promise<CreditApplication[] | undefined> {
    try {
      const result = await soulApi.soulAllApplications.requestHard(null);
      if (!result?.data) {
        throw new Error("Error on getAllApplications");
      }
      return result.data as CreditApplication[];
    } catch (error) {
      console.error("Error en getAllApplications:", error);
      throw new Error("Error en getAllApplications");
    }
  }

  async createSoulApplication(
    payload: Partial<CreditApplication>
  ): Promise<boolean | undefined> {
    try {
      const request = new ApplicationRequest({ app: payload });
      const result = await soulApi.createSoulApplication.request(request);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en createSoulApplication:", error);
      throw new Error("Error en createSoulApplication");
    }
  }

  async updateSoulApplication(
    payload: Partial<CreditApplication>
  ): Promise<boolean | undefined> {
    try {
      const request = new ApplicationRequest({ app: payload });
      const result = await soulApi.updateSoulApplication.request(request);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en updateSoulApplication:", error);
      throw new Error("Error en updateSoulApplication");
    }
  }

  async enableConsentApplication(id: string): Promise<boolean | undefined> {
    try {
      const request = new ApplicationRequest({
        app: {
          id: id,
          isConsentAvailable: true,
        },
      });
      const result = await soulApi.updateSoulApplication.request(request);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en updateSoulApplication:", error);
      throw new Error("Error en updateSoulApplication");
    }
  }

  async declineApplication(id: string): Promise<boolean | undefined> {
    try {
      const request = new ApplicationRequest({
        app: { id: id, isConsentAvailable: false, status: "REJECTED" },
      });
      const result = await soulApi.updateSoulApplication.request(request);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en declineApplication:", error);
      throw new Error("Error en declineApplication");
    }
  }

  async finalizeApplication(
    id: string,
    qualification: number
  ): Promise<boolean | undefined> {
    try {
      const request = new ApplicationRequest({
        app: { id: id, qualification, status: "FINISHED" },
      });
      const result = await soulApi.updateSoulApplication.request(request);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en finalizeApplication:", error);
      throw new Error("Error en finalizeApplication");
    }
  }

  async updateSoulApplicationByAdmin(
    payload: Partial<CreditApplication>
  ): Promise<boolean | undefined> {
    try {
      const request = new ApplicationRequest({ app: payload });
      const result = await soulApi.updateSoulApplicationByAdmin.request(
        request
      );
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en updateSoulApplicationByAdmin:", error);
      throw new Error("Error en updateSoulApplicationByAdmin");
    }
  }

  async createInstallmentApplication({
    applicationId,
    installmentId,
    url,
  }: {
    applicationId: string;
    installmentId: string;
    url: string;
  }): Promise<boolean | undefined> {
    try {
      const request = new InstallmentRequest({
        applicationId,
        installmentId,
        url,
        installmentStatus: "PENDING",
      });
      const result = await soulApi.createInstallmentApplication.requestHard(
        request
      );
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en createInstallmentApplication:", error);
      throw new Error("Error en createInstallmentApplication");
    }
  }

  async updateInstallmentApplication({
    applicationId,
    installmentId,
    interestAmount,
    capitalAmount,
    isPaymentInstallment,
    rejectReason,
    isRejected,
  }: {
    applicationId: string;
    installmentId: string;
    interestAmount: number;
    capitalAmount: number;
    isPaymentInstallment: boolean;
    rejectReason?: string;
    isRejected: boolean;
  }): Promise<boolean | undefined> {
    try {
      const request = new UpdateInstallmentRequest({
        applicationId,
        installmentId,
        interestAmount,
        capitalAmount,
        isPaymentInstallment,
        rejectReason,
        isRejected,
      });
      const result = await soulApi.updateInstallmentApplication.request(
        request
      );
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en updateInstallmentApplication:", error);
      throw new Error("Error en updateInstallmentApplication");
    }
  }
  async createSoulApplicationByAdmin(payload: any, targetUserId: string): Promise<boolean | undefined> {
    try {
      const request = new ApplicationAdminRequest({ app: payload, targetUserId });
      const result = await soulApi.createSoulApplicationByAdmin.request(request);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en createSoulApplicationByAdmin:", error);
      throw new Error("Error en createSoulApplicationByAdmin");
    }
  }


  async createLeverage(payload: { amount: number; interestRate: number; description: string; date: string; type: string }): Promise<boolean | undefined> {
    try {
      const result = await soulApi.createLeverage.request(payload);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en createLeverage:", error);
      throw new Error("Error en createLeverage");
    }
  }

  async getLeverages(): Promise<any[] | undefined> {
    try {
      const result = await soulApi.getLeverages.request(null);
      return result?.data;
    } catch (error) {
      console.error("Error en getLeverages:", error);
      return undefined;
    }
  }

  async updateLeverage(payload: { id: string; status: string }): Promise<boolean | undefined> {
    try {
      const result = await soulApi.updateLeverage.request(payload);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en updateLeverage:", error);
      return undefined;
    }
  }


  async updatePaymentDate(id: string, paymentDate: string): Promise<boolean | undefined> {
    try {
      const result = await soulApi.updatePaymentDate.request({ app: { id, paymentDate } });
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en updatePaymentDate:", error);
      return undefined;
    }
  }


  async anularMora(id: string): Promise<boolean | undefined> {
    try {
      const result = await soulApi.anularMora.request({ app: { id } });
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en anularMora:", error);
      return undefined;
    }
  }

  async createExpense(payload: { amount: number; description: string; date: string }): Promise<boolean | undefined> {
    try {
      const result = await soulApi.createExpense.request(payload);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en createExpense:", error);
      throw new Error("Error en createExpense");
    }
  }

  async getExpenses(): Promise<any[] | undefined> {
    try {
      const result = await soulApi.getExpenses.requestHard(null);
      return result?.data;
    } catch (error) {
      console.error("Error en getExpenses:", error);
      return undefined;
    }
  }

  async deleteExpense(id: string): Promise<boolean | undefined> {
    try {
      const result = await soulApi.deleteExpense.request({ id });
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en deleteExpense:", error);
      return undefined;
    }
  }

  async createPersonalLoan(payload: { amount: number; description: string; date: string }): Promise<boolean | undefined> {
    try {
      const result = await soulApi.createPersonalLoan.request(payload);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en createPersonalLoan:", error);
      throw new Error("Error en createPersonalLoan");
    }
  }

  async getPersonalLoans(): Promise<any[] | undefined> {
    try {
      const result = await soulApi.getPersonalLoans.request(null);
      return result?.data;
    } catch (error) {
      console.error("Error en getPersonalLoans:", error);
      return undefined;
    }
  }

  async updatePersonalLoan(payload: { id: string; status: string }): Promise<boolean | undefined> {
    try {
      const result = await soulApi.updatePersonalLoan.request(payload);
      return result?.isSuccess;
    } catch (error) {
      console.error("Error en updatePersonalLoan:", error);
      return undefined;
    }
  }

}

export const soulManager = SoulManager.get();

